import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Save, X } from 'lucide-react';
import { getUserProfile, updateUserProfile, UserProfile, UpdateProfileData } from '../api/profile-api';
import { handleApiError } from '@/shared/api/requests';

export const ProfileForm: React.FC = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<UpdateProfileData>({});

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getUserProfile();
      setProfile(data);
      setFormData({
        first_name: data.first_name,
        last_name: data.last_name,
        job_title: data.job_title,
        phone_number: data.phone_number,
      });
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!profile) return;

    setSaving(true);
    try {
      const updated = await updateUserProfile(formData);
      setProfile(updated);
      setIsEditing(false);
    } catch (err) {
      const errorMessage = handleApiError(err);
      alert(`Failed to update profile: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (!profile) return;
    setFormData({
      first_name: profile.first_name,
      last_name: profile.last_name,
      job_title: profile.job_title,
      phone_number: profile.phone_number,
    });
    setIsEditing(false);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !profile) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center text-destructive">
            {error || 'Failed to load profile'}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Profile Information</CardTitle>
          {!isEditing && (
            <Button onClick={() => setIsEditing(true)} variant="outline" size="sm">
              Edit Profile
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Email - Immutable */}
          <div>
            <Label htmlFor="email" className="text-sm font-medium text-foreground">
              Email Address
            </Label>
            <Input
              id="email"
              type="email"
              value={profile.email}
              disabled
              className="mt-1 bg-muted"
            />
            <p className="text-xs text-muted-foreground mt-1">Email cannot be changed</p>
          </div>

          {/* First Name - Mutable */}
          <div>
            <Label htmlFor="first_name" className="text-sm font-medium text-foreground">
              First Name
            </Label>
            <Input
              id="first_name"
              type="text"
              value={formData.first_name || ''}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              disabled={!isEditing}
              className="mt-1"
            />
          </div>

          {/* Last Name - Mutable */}
          <div>
            <Label htmlFor="last_name" className="text-sm font-medium text-foreground">
              Last Name
            </Label>
            <Input
              id="last_name"
              type="text"
              value={formData.last_name || ''}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              disabled={!isEditing}
              className="mt-1"
            />
          </div>

          {/* Job Title - Mutable */}
          <div>
            <Label htmlFor="job_title" className="text-sm font-medium text-foreground">
              Job Title
            </Label>
            <Input
              id="job_title"
              type="text"
              value={formData.job_title || ''}
              onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
              disabled={!isEditing}
              className="mt-1"
            />
          </div>

          {/* Phone Number - Mutable (Optional) */}
          <div>
            <Label htmlFor="phone_number" className="text-sm font-medium text-foreground">
              Phone Number
            </Label>
            <Input
              id="phone_number"
              type="tel"
              placeholder="1234567890"
              value={formData.phone_number || ''}
              onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
              disabled={!isEditing}
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">Format: 10 digits (1234567890)</p>
          </div>

          {/* Client - Immutable */}
          <div>
            <Label htmlFor="client" className="text-sm font-medium text-foreground">
              Client
            </Label>
            <Input
              id="client"
              type="text"
              value={profile.client.name}
              disabled
              className="mt-1 bg-muted"
            />
            <p className="text-xs text-muted-foreground mt-1">Client cannot be changed</p>
          </div>

          {/* Action Buttons */}
          {isEditing && (
            <div className="flex gap-3 pt-4 border-t border-border">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="flex-1"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
              <Button
                onClick={handleCancel}
                disabled={saving}
                variant="destructive"
                className="flex-1"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

